import { AxiosResponse } from "axios";
import Order, { OrderStatus } from "../model/order";
import { getAxiosInstance } from "./api-utils";
import { OrderHit } from "@/model/order-hit.schema";
import { Model } from "@/base-components/hooks/useModel";
import { UUIDTypes } from "uuid";
import { getBaseURL } from "@/generic/config";
import { Paged, PAGE_SIZE } from "@/model/paged";
const ORDERS = "/orders";

export async function searchForOrders(keywords: string, status: OrderStatus | null, scheduledDateFrom?: string | null, scheduledDateTo?: string | null, sortOrder?: string | null, page = 0, size = PAGE_SIZE): Promise<Paged<OrderHit>> {
    const response: AxiosResponse<Paged<OrderHit>> = await getAxiosInstance().get(ORDERS, { params: { keywords, status, scheduledDateFrom: scheduledDateFrom || undefined, scheduledDateTo: scheduledDateTo || undefined, sortOrder: sortOrder || undefined, page, size } });
    return response.data;
}

export async function getOrder(id: number): Promise<Order> {
    const response: AxiosResponse<Order> = await getAxiosInstance().get(`${ORDERS}/${id}`);
    return response.data;
}

export async function saveOrder(model: { object: Order, initialObject: Order }) {
    const order: Order = model.object;
    const response = await getAxiosInstance().patch(ORDERS, order);
    if (response.status === 200) {
        // Attach new files
        const files = order.attachedFiles.map(file => file.file).filter(file => !(!file));
        await Promise.all(files.map(file => uploadAttachmentToOrder(order.id, file)));

        // Remove removed files
        const removedFiles = model.initialObject.attachedFiles.filter(file => !order.attachedFiles.some(f => f.uuid === file.uuid));
        await Promise.all(removedFiles.map(file => deleteAttachmentFromOrder(order.id, file.uuid)));
    }
    return response.data;
}

export async function createOrder(order: Order) {
    const response: AxiosResponse<Order> = await getAxiosInstance().put(ORDERS, order);
    return response.data.id;
}

export async function getOverview(startDate: Date, endDate: Date, resourceId: number | null, skipBlocked?: boolean): Promise<OrderHit[]> {
    const response: AxiosResponse<OrderHit[]> = await getAxiosInstance().get(`${ORDERS}/overview`, {
        params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            resourceId: resourceId,
            skipBlocked: skipBlocked ?? false,
        }
    });
    return response.data;
}

export interface DayCount {
    date: string; // yyyy-MM-dd
    count: number;
}

export async function getDailyCounts(startDate: Date, endDate: Date, resourceId: number | null, skipBlocked = true): Promise<DayCount[]> {
    const response: AxiosResponse<DayCount[]> = await getAxiosInstance().get(`${ORDERS}/overview/daily-counts`, {
        params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            resourceId: resourceId,
            skipBlocked: skipBlocked,
        }
    });
    return response.data;
}

export async function updateScheduledDate(orderId: number, newDate: Date) {
    await getAxiosInstance().post(`${ORDERS}/${orderId}/_updateScheduledDate`, null, {
        params: { scheduledDate: newDate }
    });
}

export async function updatePriority(orderId: number, newPriority: number) {
    await getAxiosInstance().post(`${ORDERS}/${orderId}/_updatePriority`, null, {
        params: { priority: newPriority }
    });
}

export async function uploadAttachmentToOrder(orderId: number, file: File) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("objectId", orderId.toString()); // add this

    await getAxiosInstance({ excludeContentType: true }).post(`${ORDERS}/${orderId}/_uploadAttachment`, formData);
}

export async function deleteAttachmentFromOrder(orderId: number, attachmentId: UUIDTypes) {
    await getAxiosInstance().delete(`${ORDERS}/${orderId}/attachments/${attachmentId}`);
}

export async function downloadAttachmentFromOrder(orderId: number, attachmentId: UUIDTypes) {
    const url = await previewAttachmentFromOrder(orderId, attachmentId);

    // Open in new tab (browser decides to display or download)
    window.open(url, "_blank");

    // Optionally revoke the URL after some time to free memory
    setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export async function previewAttachmentFromOrder(orderId: number, attachmentId: UUIDTypes) {
    const res = await getAxiosInstance().get(`/orders/${orderId}/attachments/${attachmentId}`, {
        responseType: 'blob'
    });

    const blob = new Blob([res.data], { type: res.headers["content-type"] });
    const url = URL.createObjectURL(blob);

    return url;
}

// export async function deleteOrder(order: OrderHit) {
//     await API.delete(`${ORDERS}/${order.id}`);
//     QUERY_CLIENT.invalidateQueries(QueryKeys.orders);
// }

// export async function updateOrder(order: OrderHit) {
//     await API.patch(`${ORDERS}`, order);
//     QUERY_CLIENT.invalidateQueries(QueryKeys.orders);
// }

// export async function createOrder(order: Order) {
//     await API.put(`${ORDERS}`, order);
//     QUERY_CLIENT.invalidateQueries(QueryKeys.orders);
// }
